import { Box, Paper, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { formatShortDate, getCurrentWeekRange, isSameDay } from '../../../../utils/calendarUtils';
import { CalendarBooking } from '../SitterCalendar';

// Upcoming Events Component
interface UpcomingEventsProps {
  bookings: CalendarBooking[];
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ bookings }) => {
  const { start, end } = getCurrentWeekRange();

  const upcomingBookings = bookings
    .filter((booking) => {
      return (
        (booking.startDate.isAfter(start) || booking.startDate.isSame(start)) &&
        (booking.startDate.isBefore(end) || booking.startDate.isSame(end)) &&
        booking.status !== 'cancelled'
      );
    })
    .sort((a, b) => a.startDate.unix() - b.startDate.unix())
    .slice(0, 5);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return '#178B8E';
      case 'pending':
        return '#FFA726';
      default:
        return '#9e9e9e';
    }
  };

  const formatDateRange = (startDate: Dayjs, endDate: Dayjs) => {
    if (isSameDay(startDate, endDate)) {
      return formatShortDate(startDate);
    }
    const start = formatShortDate(startDate);
    const end = formatShortDate(endDate);
    return `${start} - ${end}`;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Upcoming This Week
      </Typography>
      {upcomingBookings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No upcoming bookings this week
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {upcomingBookings.map((booking) => (
            <Box
              key={booking.id}
              sx={{
                borderLeft: `4px solid ${getStatusColor(booking.status)}`,
                pl: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatDateRange(booking.startDate, booking.endDate)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {booking.petName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {booking.service} • ${booking.price}
              </Typography>
              {booking.status === 'pending' && (
                <Typography variant="caption" sx={{ color: '#FFA726', fontWeight: 600 }}>
                  (Pending)
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default UpcomingEvents;
